import { Box, Checkbox, FormControlLabel, Drawer, Typography, IconButton, Stack } from '@mui/material';
import { useGetBusinesses } from 'apis/agency/hook';
import { useConfigureIntegration, useGetAvailableIntegration } from 'apis/agency/integrations/hook';
import { ConfigureIntegrationPayload } from 'apis/agency/integrations/types';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Search from 'components/Common/Search';
import ConfigureSkeleton from './ConfigureSkeleton';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { BuilderEmptyStateView } from '@/components/Common/BuilderEmptyStateView';
import PasswordInput from '@/components/Common/PasswordInput';
import GoogleOAuthConfigInfo from './GoogleOAuthConfigInfo';

type GoogleCalendarConfigForm = {
  client_id: string;
  client_secret: string;
};

export default function Configure() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<null | string>(null);
  const { data: businesses, isLoading } = useGetBusinesses({
    per_page: 9999,
    filter: { active_and_past_due: true, name_cont: searchText }
  });
  // Todo : Add infiniteScroll here
  const { mutateAsync: configureIntegration } = useConfigureIntegration();
  const { data: integration, isLoading: isIntegrationLoading } = useGetAvailableIntegration();

  const isGoogleCalendar = integration?.code === 'google_calendar';
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const { control, handleSubmit, reset } = useForm<GoogleCalendarConfigForm>({
    defaultValues: {
      client_id: '',
      client_secret: ''
    }
  });

  useEffect(() => {
    if (!integration) return;
    setCheckedItems(integration.business_ids ?? []);
    if (integration.code === 'google_calendar') {
      reset({
        client_id: integration.configuration?.client_id ?? '',
        client_secret: integration.configuration?.client_secret ?? ''
      });
    }
  }, [integration, reset]);

  const handleCheckboxChange = (knowledgeBaseId: number) => {
    setCheckedItems((prev) =>
      prev.includes(knowledgeBaseId) ? prev.filter((itemId) => itemId !== knowledgeBaseId) : [...prev, knowledgeBaseId]
    );
  };

  const handleCloseModal = () => navigate(-1);

  const handleConfigureIntegration = async (formValues?: GoogleCalendarConfigForm) => {
    const payload: ConfigureIntegrationPayload = {
      business_ids: checkedItems
    };

    if (isGoogleCalendar && formValues) {
      payload.configuration = {
        client_id: formValues.client_id.trim(),
        client_secret: formValues.client_secret.trim()
      };
    }

    await configureIntegration(payload);
    handleCloseModal();
  };

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => handleCloseModal()}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 830 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Box>
          <Typography variant='h6' fontWeight={500}>
            {isGoogleCalendar ? 'Configure Integration' : 'Select Businesses'}
          </Typography>
        </Box>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 3.5,
          pt: 3,
          pb: 2
        }}
      >
        {isGoogleCalendar && (
          <Stack spacing={2} mb={3}>
            <Typography fontWeight={600} fontSize={14}>
              Pre-Configuration
            </Typography>
            <Input
              name='client_id'
              control={control}
              label='Client ID'
              placeholder='Enter Here'
              required
              fullWidth
              rules={{ required: 'Client ID is required' }}
            />
            <PasswordInput
              name='client_secret'
              control={control}
              label='Client Secret'
              placeholder='Enter Here'
              required
              fullWidth
              rules={{ required: 'Client Secret is required' }}
            />
          </Stack>
        )}

        {isGoogleCalendar && (
          <Typography mb={2} fontWeight={600} fontSize={14}>
            Assign integration to businesses
          </Typography>
        )}
        <Search fullWidth label='Search Businesses' searchText={searchText} setSearchText={setSearchText} />

        {isLoading || isIntegrationLoading ? (
          <ConfigureSkeleton />
        ) : (
          <Box mt={2}>
            {businesses?.data?.length ? (
              businesses.data.map((item, index) => (
                <Box key={index}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={item.name}
                        value={item.id}
                        checked={checkedItems.includes(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                      />
                    }
                    label={
                      <Box lineHeight={1.2} mb={0.5}>
                        <Typography mb={0} variant='body1'>
                          {item.name}
                        </Typography>
                        <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.1 }}>
                          ({item.email})
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))
            ) : (
              <BuilderEmptyStateView message='No business found' />
            )}
          </Box>
        )}

        {isGoogleCalendar && (
          <Box mt={3}>
            <GoogleOAuthConfigInfo />
          </Box>
        )}
      </Box>

      {/* Fixed Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #DEDEDE',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}
      >
        <Button color='secondary' variant='outlined' onClick={() => handleCloseModal()}>
          Cancel
        </Button>
        <Button
          onClick={
            isGoogleCalendar
              ? handleSubmit((values) => handleConfigureIntegration(values))
              : () => handleConfigureIntegration()
          }
        >
          Submit
        </Button>
      </Box>
    </Drawer>
  );
}
